import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { adminApi, SignupRequestDetails, SearchParams } from '../services/adminApi';
import {
  PageHeader,
  PageTitle,
  FilterGroup,
  SearchInput,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  Table,
  Th,
  Td,
  Tr,
  StatusBadge,
  IconButton,
  ButtonGroup,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseButton,
  FormGroup,
  Label,
  Input,
  Textarea,
  AlertMessage,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  LoadingSpinner,
  Card,
  CardBody,
  PaginationContainer,
  PaginationInfo,
  PaginationButtons,
} from '../styles/adminShared';

// Page-specific styled components
const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
`;

const StatusFilterButton = styled.button<{ $active: boolean }>`
  padding: 12px 20px;
  border-radius: 8px;
  border: ${props => props.$active ? 'none' : '1px solid var(--ds-border)'};
  background: ${props => props.$active ? 'var(--ds-primary)' : 'var(--ds-surface)'};
  color: ${props => props.$active ? 'white' : 'var(--ds-text-primary)'};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 12px;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: var(--ds-text-secondary);
  margin-bottom: 8px;
  margin-top: 16px;

  &:first-child {
    margin-top: 0;
  }
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.div`
  font-size: 12px;
  color: var(--ds-text-secondary);
`;

const FieldValue = styled.div`
  font-weight: 500;
  color: var(--ds-text-primary);
`;

type StatusFilter = 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED';

/**
 * Super Admin page for managing organization signup requests.
 * Only visible to platform tenant admins.
 */
export const SignupRequests: React.FC = () => {
  const [requests, setRequests] = useState<SignupRequestDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('PENDING');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<SignupRequestDetails | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params: SearchParams = {
        page,
        pageSize,
        query: searchQuery || undefined,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      };
      
      const response = await adminApi.getSignupRequests(params);
      setRequests(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, statusFilter]);

  const fetchStatusCounts = useCallback(async () => {
    try {
      const counts = await adminApi.getSignupRequestCounts();
      setStatusCounts(counts);
    } catch (err) {
      console.error('Failed to fetch status counts', err);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchStatusCounts();
  }, [fetchRequests, fetchStatusCounts]);

  const handleApprove = async (requestId: string, customSlug?: string) => {
    setActionLoading(true);
    try {
      await adminApi.approveSignupRequest(requestId, { customSlug });
      setSelectedRequest(null);
      fetchRequests();
      fetchStatusCounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (requestId: string, reason: string) => {
    setActionLoading(true);
    try {
      await adminApi.rejectSignupRequest(requestId, { rejectionReason: reason });
      setSelectedRequest(null);
      fetchRequests();
      fetchStatusCounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <PageContainer>
      <Card>
        <CardBody>
          <PageHeader>
            <div>
              <PageTitle>Organization Signup Requests</PageTitle>
            </div>
          </PageHeader>

          {/* Status counts */}
          <FilterGroup style={{ marginBottom: 24 }}>
            {['PENDING', 'APPROVED', 'REJECTED'].map(status => (
              <StatusFilterButton
                key={status}
                $active={statusFilter === status}
                onClick={() => {
                  setStatusFilter(status as StatusFilter);
                  setPage(1);
                }}
              >
                {status} ({statusCounts[status] || 0})
              </StatusFilterButton>
            ))}
            <StatusFilterButton
              $active={statusFilter === 'ALL'}
              onClick={() => {
                setStatusFilter('ALL');
                setPage(1);
              }}
            >
              ALL ({Object.values(statusCounts).reduce((a, b) => a + b, 0)})
            </StatusFilterButton>
          </FilterGroup>

          {/* Search */}
          <div style={{ marginBottom: 16 }}>
            <SearchInput
              type="text"
              placeholder="Search by organization name, email, or contact..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {error && (
            <AlertMessage $variant="error">
              {error}
            </AlertMessage>
          )}

          {/* Table */}
          {loading ? (
            <LoadingSpinner>Loading...</LoadingSpinner>
          ) : requests.length === 0 ? (
            <EmptyStateContainer>
              <EmptyStateIcon>📋</EmptyStateIcon>
              <EmptyStateTitle>No requests found</EmptyStateTitle>
            </EmptyStateContainer>
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <Tr>
                    <Th>Organization</Th>
                    <Th>Contact</Th>
                    <Th>Type</Th>
                    <Th>Submitted</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </Tr>
                </thead>
                <tbody>
                  {requests.map((req) => (
                    <Tr key={req.requestId}>
                      <Td>
                        <div style={{ fontWeight: 500 }}>{req.organizationName}</div>
                        {req.organizationWebsite && (
                          <div style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
                            {req.organizationWebsite}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <div>{req.contactName}</div>
                        <div style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
                          {req.contactEmail}
                        </div>
                      </Td>
                      <Td>{req.organizationType || '-'}</Td>
                      <Td>
                        {req.submittedAt ? new Date(req.submittedAt).toLocaleDateString() : '-'}
                      </Td>
                      <Td>
                        <StatusBadge $status={req.status}>
                          {req.status}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <IconButton
                          $variant="view"
                          onClick={() => setSelectedRequest(req)}
                        >
                          👁️ View
                        </IconButton>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationInfo>
                Page {page} of {totalPages}
              </PaginationInfo>
              <PaginationButtons>
                <SecondaryButton
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </SecondaryButton>
                <SecondaryButton
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </SecondaryButton>
              </PaginationButtons>
            </PaginationContainer>
          )}
        </CardBody>
      </Card>

      {/* Detail Modal */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          loading={actionLoading}
        />
      )}
    </PageContainer>
  );
};

interface RequestDetailModalProps {
  request: SignupRequestDetails;
  onClose: () => void;
  onApprove: (requestId: string, customSlug?: string) => void;
  onReject: (requestId: string, reason: string) => void;
  loading: boolean;
}

const RequestDetailModal: React.FC<RequestDetailModalProps> = ({
  request,
  onClose,
  onApprove,
  onReject,
  loading,
}) => {
  const [customSlug, setCustomSlug] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  return (
    <ModalOverlay $show={true} onClick={onClose}>
      <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Request Details</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          <Section title="Organization">
            <Field label="Name" value={request.organizationName} />
            <Field label="Website" value={request.organizationWebsite || '-'} />
            <Field label="Type" value={request.organizationType || '-'} />
          </Section>

          <Section title="Contact Person">
            <Field label="Name" value={request.contactName} />
            <Field label="Email" value={request.contactEmail} />
            <Field label="Phone" value={request.contactPhone || '-'} />
            <Field label="Role" value={request.contactRole || '-'} />
          </Section>

          <Section title="Additional Info">
            <Field label="Expected Users" value={request.expectedUsers || '-'} />
            <Field label="Use Case" value={request.useCase || '-'} />
          </Section>

          <Section title="Request Info">
            <Field label="Status" value={request.status} />
            <Field label="Submitted" value={request.submittedAt ? new Date(request.submittedAt).toLocaleString() : '-'} />
            <Field label="IP Address" value={request.ipAddress || '-'} />
          </Section>

          {request.status === 'APPROVED' && (
            <Section title="Tenant Created">
              <Field label="Tenant ID" value={request.createdTenantId || '-'} />
              <Field label="Slug" value={request.createdTenantSlug || '-'} />
              <Field label="Login URL" value={request.createdTenantSlug ? `/t/${request.createdTenantSlug}/login` : '-'} />
            </Section>
          )}

          {request.status === 'REJECTED' && (
            <Section title="Rejection">
              <Field label="Reason" value={request.rejectionReason || '-'} />
              <Field label="Reviewed By" value={request.reviewedBy || '-'} />
            </Section>
          )}
        </ModalBody>

        {request.status === 'PENDING' && (
          <ModalFooter>
            {!showRejectForm ? (
              <>
                <FormGroup style={{ width: '100%', marginBottom: 0 }}>
                  <Label htmlFor="customSlug">Custom Slug (optional)</Label>
                  <Input
                    id="customSlug"
                    type="text"
                    placeholder="e.g., acme-corp"
                    value={customSlug}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setCustomSlug(e.target.value)}
                  />
                  <span style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
                    Leave empty to auto-generate based on organization name
                  </span>
                </FormGroup>
                <ButtonGroup>
                  <PrimaryButton
                    onClick={() => onApprove(request.requestId, customSlug || undefined)}
                    disabled={loading}
                  >
                    Approve & Create Tenant
                  </PrimaryButton>
                  <DangerButton
                    onClick={() => setShowRejectForm(true)}
                    disabled={loading}
                  >
                    Reject
                  </DangerButton>
                </ButtonGroup>
              </>
            ) : (
              <>
                <FormGroup style={{ width: '100%', marginBottom: 0 }}>
                  <Label htmlFor="rejectionReason">Rejection Reason *</Label>
                  <Textarea
                    id="rejectionReason"
                    placeholder="Explain why this request is being rejected..."
                    value={rejectionReason}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setRejectionReason(e.target.value)}
                    rows={3}
                  />
                </FormGroup>
                <ButtonGroup>
                  <DangerButton
                    onClick={() => onReject(request.requestId, rejectionReason)}
                    disabled={loading || !rejectionReason.trim()}
                  >
                    Confirm Rejection
                  </DangerButton>
                  <SecondaryButton
                    onClick={() => setShowRejectForm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </SecondaryButton>
                </ButtonGroup>
              </>
            )}
          </ModalFooter>
        )}

        {request.status !== 'PENDING' && (
          <ModalFooter>
            <SecondaryButton onClick={onClose}>
              Close
            </SecondaryButton>
          </ModalFooter>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <SectionTitle>{title}</SectionTitle>
    <SectionGrid>
      {children}
    </SectionGrid>
  </div>
);

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <FieldRow>
    <FieldLabel>{label}</FieldLabel>
    <FieldValue>{value}</FieldValue>
  </FieldRow>
);

export default SignupRequests;
